import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolButton, ToolAddFileButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { ListLine } from "../framework/listLine";
import { FileType } from "ui/Scripts/source/models/enums";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";

//AttachmentListMenu
export interface IAttachmentListMenuProp {
    isReadOnly: boolean;
    item: IFileAttachment;
    onDownload: () => void;
    onRemove: () => void;
    onCancel: () => void;
}

export class AttachmentListMenu extends React.Component<IAttachmentListMenuProp, {}> {
    handleDownloadClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onDownload();
    };

    handleRemoveClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onRemove();
    };

    handleCancelClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onCancel();
    };
        
    render() {
        const item = this.props.item;
        if (Base.isNullOrUndefined(item)) return null;
        const isReadOnly = this.props.isReadOnly;
        const canDownload = !item.isNew();
        return (
            <div className="listPopupMenu" onClick={this.handleCancelClick}>
                <div className="menu">
                    <div className={"line" + (canDownload ? "" : " disabled")} onClick={canDownload ? this.handleDownloadClick : null}>{Translations.Download}</div>
                    <div className={"line" + (!isReadOnly ? "" : " disabled")} onClick={!isReadOnly ? this.handleRemoveClick : null}>{Translations.Remove}</div>
                    <div className="line" onClick={this.handleCancelClick}>{Translations.Cancel}</div>
                </div>
            </div>
        );
    }
}

//AttachmentImage
export interface IAttachmentImageProp {
    lateThreshold: number;
    attachment: IFileAttachment;
    selectedId: string;
    onClick: (id: string) => void;
    onLongPress: (id: string) => void;
}

export class AttachmentImage extends React.Component<IAttachmentImageProp, {}> {
    private imgElement: HTMLImageElement;

    handleContextMenu = (event) => {
        event.preventDefault();
    };

    componentDidMount(): void {
        this.imgElement.addEventListener("contextmenu", this.handleContextMenu);
    }

    componentWillUnmount(): void {
        this.imgElement.removeEventListener("contextmenu", this.handleContextMenu);
    }

    handleDownloadClick = () => {
        this.props.onClick(this.props.attachment.id);
    };

    render() {
        const timeToleranceInJsTicks = 500;
        const lateThreshold = this.props.lateThreshold;
        const attachment = this.props.attachment;
        const isLate = !Base.isNullOrUndefined(lateThreshold) && attachment.modifyDate > (lateThreshold + timeToleranceInJsTicks);
        return (
            <ListLine
                id={attachment.id}
                first={false}
                selected={this.props.selectedId === attachment.id}
                classes={"attachmentImage" + (isLate ? " late" : "")}
                onClick={this.props.onClick}
                onLongPress={this.props.onLongPress}
                onRefreshList={null}>
                <img src={attachment.thumbnail} alt={attachment.name} ref={(elem) => { this.imgElement = elem; }}/>
            </ListLine>
        );
    }
}

//AttachmentListLine
export interface IAttachmentListLineProp {
    lateThreshold: number;
    attachment: IFileAttachment;
    selectedId: string;
    onClick: (id: string) => void;
    onLongPress: (id: string) => void;
}

export class AttachmentListLine extends React.Component<IAttachmentListLineProp, {}> {
    render() {
        const timeToleranceInJsTicks = 500;
        const lateThreshold = this.props.lateThreshold;
        const attachment = this.props.attachment;
        const isLate = !Base.isNullOrUndefined(lateThreshold) && attachment.modifyDate > (lateThreshold + timeToleranceInJsTicks);
        return (
            <ListLine
                id={attachment.id}
                first={false}
                selected={this.props.selectedId === attachment.id}
                classes={"attachment" + (isLate ? " late" : "")}
                onClick={this.props.onClick}
                onLongPress={this.props.onLongPress}
                onRefreshList={null}>
                <span className={"icon file file_type_" + attachment.type}></span>
                <span className="name">{attachment.name}</span>
                <span>(</span>
                <span className="size">{Base.intToFileSizeStr(attachment.size, false)}</span>
                {isLate &&
                    <span>, </span>
                }
                {isLate &&
                    <span className="modifyDate">{Base.timeToDateStr(attachment.modifyDate)}</span>
                }
                <span>)</span>
            </ListLine>
        );
    }
}

//AttachmentList
export interface IAttachmentListProp {
    classes?: string;
    lateThreshold?: number;
    attachments: IFileAttachment[];
    isReadOnly: boolean;
    canShowCamera: boolean;
    onRemoveAttachment: (id: string) => void;
    onDownloadAttachment: (id: string) => void;
    onAddAttachments: (fileList: FileList) => void;
    onAddPhoto: () => void;
}

interface IAttachmentListState {
    selectedId: string;
    showListMenu: boolean;
}

export class AttachmentList extends React.Component<IAttachmentListProp, IAttachmentListState> {
    constructor(props) {
        super(props);
        this.state = { selectedId: null, showListMenu: false };
    }
    
    handleAddClick = (fileList: FileList) => {
        this.props.onAddAttachments(fileList);
    };

    handleAddPhotoClick = () => {
        this.props.onAddPhoto();
    };

    handleMenuDownload = () => {
        const selectedId = this.state.selectedId;
        if (Base.isNullOrEmpty(selectedId)) return;
        this.props.onDownloadAttachment(selectedId);
        this.setState({
            showListMenu: false
        });
    };

    handleMenuRemove = () => {
        const selectedId = this.state.selectedId;
        if (Base.isNullOrEmpty(selectedId)) return;
        this.props.onRemoveAttachment(selectedId);
        this.setState({
            showListMenu: false
        });
    };

    handleAttachmentClick = (id: string) => {
        this.setState({
            selectedId: id
        });
    };

    handleAttachmentLongPress = (id: string) => {
        this.setState({
            selectedId: id,
            showListMenu: true
        });
    };

    handleMenuCancel = () => {
        this.setState({
            showListMenu: false
        });
    };

    render() {
        const lateThreshold = this.props.lateThreshold;
        const attachments = this.props.attachments;
        const selectedId = this.state.selectedId;
        const selectedAttachment = attachments.find(i => i.id === selectedId);
        const imageAttachments = attachments.filter(i => i.type === FileType.Image);
        const fileAttachments = attachments.filter(i => i.type !== FileType.Image);
        return (
            <div className={this.props.classes}>
                <div className="fullLeft">
                    <label className="control-label">{Translations.Attachments}</label>
                    {!this.props.isReadOnly &&
                        <ToolAddFileButton
                            tooltip={Translations.Add}
                            fileInputName="attachments"
                            classes={"round right mono add"}
                            onChange={this.handleAddClick}
                        />
                    }
                    {!this.props.isReadOnly && this.props.canShowCamera &&
                        <ToolButton
                            tooltip={Translations.AddPhoto}
                            classes={"round right mono camera"}
                            onClick={this.handleAddPhotoClick}
                        />
                    }
                </div>
                {!this.props.isReadOnly && attachments.length === 0 &&
                    <div className="fullLeft">
                        <div className="alert alert-info" role="alert">
                            {this.props.canShowCamera ? Translations.AddPhotosAndAttachementsToEvent : Translations.AddAttachementsToEvent}
                        </div>
                    </div>
                }
                {attachments.length > 0 &&
                    <div className="attachmentsContainer">
                        {imageAttachments.length > 0 &&
                            <div className="attachmentImages">
                                {imageAttachments.map((attachment) =>
                                    <AttachmentImage
                                        key={attachment.id}
                                        lateThreshold={lateThreshold}
                                        attachment={attachment}
                                        selectedId={this.state.selectedId}
                                        onClick={this.handleAttachmentClick}
                                        onLongPress={!attachment.isNew() || !this.props.isReadOnly ? this.handleAttachmentLongPress : null}
                                    />
                                )}
                            </div>
                        }
                        {fileAttachments.length > 0 &&
                            <div className="attachments">
                                {fileAttachments.map((attachment) =>
                                    <AttachmentListLine
                                        key={attachment.id}
                                        lateThreshold={lateThreshold}
                                        attachment={attachment}
                                        selectedId={this.state.selectedId}
                                        onClick={this.handleAttachmentClick}
                                        onLongPress={!attachment.isNew() || !this.props.isReadOnly ? this.handleAttachmentLongPress : null}
                                    />
                                )}
                            </div>
                        }
                    </div>
                }
                {this.state.showListMenu &&
                    <AttachmentListMenu
                        isReadOnly={this.props.isReadOnly}
                        item={selectedAttachment}
                        onDownload={this.handleMenuDownload}
                        onRemove={this.handleMenuRemove}
                        onCancel={this.handleMenuCancel}
                    />
                }
            </div>
        );
    }
}